import { TachographDownloadRow, TachographFile, TachographUnitLabel } from "../models/Tachograph";
import { getMostRecentDate, parseDateOrNull } from "./tachographDates";

/** Files without an own origin come from the remote download service. */
const DEFAULT_ORIGIN = "RDL";

/** The API sends the file content either as a byte array or base64 encoded. */
export function binaryToUint8Array(binaryData: number[] | string | undefined): Uint8Array | null {
  if (Array.isArray(binaryData) && binaryData.length > 0) {
    return new Uint8Array(binaryData);
  }

  if (typeof binaryData === "string" && binaryData.length > 0) {
    const decoded = atob(binaryData);
    const bytes = new Uint8Array(decoded.length);
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index);
    }

    return bytes;
  }

  return null;
}

export function pickFileName(file?: TachographFile): string {
  return file?.fileNameDdd ?? file?.fileName ?? file?.fileNameTgd ?? file?.fileNameV1B ?? "";
}

/** When the file was generated by the tachograph. */
export function getFileActivityDate(file?: TachographFile): Date | null {
  return getMostRecentDate(file?.timestampEnd, file?.timestamp, file?.dateTime);
}

/** When the file arrived in Geotab. */
export function getFileUploadDate(file?: TachographFile): Date | null {
  return parseDateOrNull(file?.dateTime);
}

/** The date the next deadline counts from: the activity end, otherwise the activity timestamp. */
export function getFileReferenceDate(file?: TachographFile): Date | null {
  return parseDateOrNull(file?.timestampEnd) ?? parseDateOrNull(file?.timestamp);
}

/** A file only counts as a readout if it carries a date or a name. */
export function hasFileData(file?: TachographFile): boolean {
  if (!file) return false;
  if (getFileActivityDate(file)) return true;

  return pickFileName(file).trim().length > 0;
}

/** Keeps the newest file per device or driver. */
export function latestFileByEntityId(
  files: TachographFile[],
  getEntityId: (file: TachographFile) => string | undefined,
): Record<string, TachographFile> {
  const latest: Record<string, TachographFile> = {};

  for (const file of files) {
    if ((typeof file.errors === "string" && file.errors.length > 0) || file.archived === true) continue;

    const entityId = getEntityId(file);
    if (!entityId) continue;

    if (!latest[entityId] || isLaterFile(file, latest[entityId])) {
      latest[entityId] = file;
    }
  }

  return latest;
}

/** Compares two files by reference date, falling back to upload date as tie-breaker. */
function isLaterFile(candidate: TachographFile, current: TachographFile): boolean {
  const candidateRef = getFileReferenceDate(candidate);
  const currentRef = getFileReferenceDate(current);

  if (candidateRef && !currentRef) return true;
  if (!candidateRef && currentRef) return false;

  if (candidateRef && currentRef) {
    if (candidateRef.getTime() !== currentRef.getTime()) {
      return candidateRef.getTime() > currentRef.getTime();
    }
    const candidateUpload = getFileUploadDate(candidate);
    const currentUpload = getFileUploadDate(current);
    if (candidateUpload && !currentUpload) return true;
    if (!candidateUpload && currentUpload) return false;
    if (candidateUpload && currentUpload) return candidateUpload.getTime() > currentUpload.getTime();
    return false;
  }

  const candidateUpload = getFileUploadDate(candidate);
  const currentUpload = getFileUploadDate(current);
  if (candidateUpload && !currentUpload) return true;
  if (!candidateUpload && currentUpload) return false;
  if (candidateUpload && currentUpload) return candidateUpload.getTime() > currentUpload.getTime();
  return false;
}

/** Keeps only the driver-card files of the given drivers, so they follow the same search as the vehicle files. */
export function filterFilesByDriverIds(
  files: TachographFile[],
  driverIds: ReadonlySet<string>,
): TachographFile[] {
  return files.filter(file => !!file.driver?.id && driverIds.has(file.driver.id));
}

/** Keeps only the vehicle files of the given devices, so they follow the same search as the vehicle rows. */
export function filterFilesByDeviceIds(
  files: TachographFile[],
  deviceIds: ReadonlySet<string>,
): TachographFile[] {
  return files.filter(file => !!file.device?.id && deviceIds.has(file.device.id));
}

function buildUnitLabel(unit?: TachographUnitLabel): string {
  if (!unit) return "";

  const { name, licensePlate } = unit;
  if (!licensePlate) return name;
  if (name.toLowerCase().includes(licensePlate.toLowerCase())) return name;

  return [licensePlate, name].filter(Boolean).join(" - ");
}

/** The rows of the downloads tab, newest file first. */
export function buildDownloadRows(
  files: TachographFile[],
  unitsByDeviceId: Map<string, TachographUnitLabel>,
): TachographDownloadRow[] {
  return files
    .map(file => {
      const id = file.id?.trim();
      if (!id) return null;

      const dataFrom = parseDateOrNull(file.timestampStart);
      const dataTo = parseDateOrNull(file.timestampEnd) ?? parseDateOrNull(file.timestamp);
      const createdAt = parseDateOrNull(file.timestamp) ?? parseDateOrNull(file.timestampEnd);
      const addedAt = parseDateOrNull(file.dateTime);
      const sortDate = addedAt ?? createdAt ?? dataTo ?? dataFrom;

      return {
        row: {
          id,
          source: file.originDescription?.trim() || DEFAULT_ORIGIN,
          fileName: pickFileName(file),
          unit: buildUnitLabel(file.device?.id ? unitsByDeviceId.get(file.device.id) : undefined),
          dataFromUtc: dataFrom,
          dataToUtc: dataTo,
          createdAt: createdAt,
          addedAt: addedAt,
        },
        sortMs: sortDate?.getTime() ?? 0,
      };
    })
    .filter((entry): entry is { row: TachographDownloadRow; sortMs: number } => entry !== null)
    .sort((a, b) => b.sortMs - a.sortMs)
    .map(entry => entry.row);
}
